/**
 * examQuestionSort.js
 * 
 * Robust exam-style question sorting utility.
 * Sorts questions into canonical exam order:
 *   1 → 1a → 1a(i) → 1a(ii) → 1a(iii) → 1b → 1c → 2 → 2a → 10 → 10a
 * 
 * Handles:
 *   - Main question numbers (1, 2, 3, 10, 11)
 *   - Letter subsections (a, b, c, d)
 *   - Roman numeral subparts (i, ii, iii, iv, v, vi, vii, viii, ix, x)
 *   - Various formatting styles: "1a", "1 a", "1A", "1a(i)", "Q1A(ii)", "Question 1A(ii)"
 */

// Roman numeral values — ordered LONGEST FIRST to prevent partial matches
// e.g., "viii" must match before "vii" before "vi" before "v" before "iv" before "iii" before "ii" before "i"
const ROMAN_MAP = [
  { str: 'viii', val: 8 },
  { str: 'vii',  val: 7 },
  { str: 'vi',   val: 6 },
  { str: 'iv',   val: 4 },
  { str: 'ix',   val: 9 },
  { str: 'v',    val: 5 },
  { str: 'x',    val: 10 },
  { str: 'iii',  val: 3 },
  { str: 'ii',   val: 2 },
  { str: 'i',    val: 1 },
];

/**
 * Parse a roman numeral string to its integer value.
 * Returns 0 if not a valid roman numeral.
 */
function parseRoman(str) {
  if (!str) return 0;
  const lower = str.toLowerCase().trim();
  for (const { str: roman, val } of ROMAN_MAP) {
    if (lower === roman) return val;
  }
  return 0;
}

/**
 * Extract a sortable key from a question label or question text.
 * 
 * Returns { mainNum, letter, roman } where:
 *   mainNum: integer (1, 2, 10, etc.) — 0 if not found
 *   letter: integer (a=1, b=2, c=3, etc.) — 0 if no letter
 *   roman: integer (i=1, ii=2, iii=3, iv=4, etc.) — 0 if no roman numeral
 */
export function parseQuestionLabel(rawLabel) {
  if (!rawLabel || typeof rawLabel !== 'string') {
    return { mainNum: 9999, letter: 0, roman: 0, raw: rawLabel || '' };
  }

  let s = rawLabel.trim();

  // Strip leading "Question " prefix (case-insensitive)
  s = s.replace(/^question\s+/i, '');

  // Strip leading "Q" prefix (e.g., "Q1a" → "1a")
  s = s.replace(/^q/i, '');

  // Remove all whitespace for uniform parsing
  s = s.replace(/\s+/g, '');

  // Lowercase for uniform matching
  s = s.toLowerCase();

  // Extract main number from the start
  const numMatch = s.match(/^(\d+)/);
  const mainNum = numMatch ? parseInt(numMatch[1], 10) : 9999;
  let rest = numMatch ? s.substring(numMatch[0].length) : s;

  // Extract letter subsection (single letter a-z)
  let letter = 0;
  const letterMatch = rest.match(/^([a-z])/);
  if (letterMatch) {
    letter = letterMatch[1].charCodeAt(0) - 96; // a=1, b=2, c=3, etc.
    rest = rest.substring(1);
  }

  // Extract roman numeral subpart — may be in brackets or standalone
  let roman = 0;
  // Try bracketed format: (i), (ii), (iii), (iv), etc.
  const bracketedRoman = rest.match(/^\(([ivxlcdm]+)\)/);
  if (bracketedRoman) {
    roman = parseRoman(bracketedRoman[1]);
  } else {
    // Try standalone roman numeral at end
    const standaloneRoman = rest.match(/^([ivxlcdm]+)$/);
    if (standaloneRoman) {
      roman = parseRoman(standaloneRoman[1]);
    }
  }

  return { mainNum, letter, roman, raw: rawLabel };
}

/**
 * Compare two parsed question keys for sorting.
 * Returns negative if a < b, positive if a > b, 0 if equal.
 */
function compareQuestionKeys(a, b) {
  // Primary: main question number
  if (a.mainNum !== b.mainNum) return a.mainNum - b.mainNum;
  // Secondary: letter subsection
  if (a.letter !== b.letter) return a.letter - b.letter;
  // Tertiary: roman numeral subpart
  if (a.roman !== b.roman) return a.roman - b.roman;
  // Fallback: alphabetical on raw label
  return (a.raw || '').localeCompare(b.raw || '', undefined, { numeric: true });
}

/**
 * Extract the best available label from a question object.
 * Tries questionLabel, questionNumber, label, then extracts from questionText.
 */
export function extractLabel(question) {
  if (!question) return '';
  
  // Try explicit label fields first
  if (question.questionLabel && typeof question.questionLabel === 'string' && question.questionLabel.trim()) {
    return question.questionLabel.trim();
  }
  if (question.questionNumber && typeof question.questionNumber === 'string' && question.questionNumber.trim()) {
    return question.questionNumber.trim();
  }
  if (question.label && typeof question.label === 'string' && question.label.trim()) {
    return question.label.trim();
  }

  // Try to extract label from the start of questionText
  if (question.questionText && typeof question.questionText === 'string') {
    const text = question.questionText.trim();
    // Match patterns like: "Question 1C(i) ...", "1a ...", "1C(ii) ...", "2a(iii) ..."
    const match = text.match(
      /^(?:question\s+)?(?:q)?(\d+[a-zA-Z]?(?:\s*\([ivxlcdmIVXLCDM]+\))?)/i
    );
    if (match && match[1]) return match[1].trim();
    
    // Try just a number at the start
    const numOnly = text.match(/^(?:question\s+)?(?:q)?(\d+)/i);
    if (numOnly && numOnly[1]) return numOnly[1];
  }

  return '';
}

/**
 * Sort an array of question IDs based on their question labels/text,
 * using exam-style ordering.
 * 
 * @param {string[]} questionIds - Array of question IDs to sort
 * @param {object[]} allQuestions - Array of all question objects (with .id, .questionText, etc.)
 * @returns {string[]} - New array of question IDs in sorted order
 */
export function sortQuestionIds(questionIds, allQuestions) {
  if (!questionIds || questionIds.length <= 1) return [...(questionIds || [])];
  if (!allQuestions || allQuestions.length === 0) return [...questionIds];

  // Build a lookup map: id → question object
  const questionMap = new Map();
  for (const q of allQuestions) {
    if (q && q.id) questionMap.set(q.id, q);
  }

  // Build sortable entries: { id, key }
  const entries = questionIds.map(id => {
    const question = questionMap.get(id);
    const label = extractLabel(question);
    const key = parseQuestionLabel(label);
    return { id, key, label };
  });

  // Log before sort
  console.log('[REORDER] Before:', entries.map(e => e.label || e.id));

  // Sort using the comparison function
  entries.sort((a, b) => compareQuestionKeys(a.key, b.key));

  // Log after sort
  console.log('[REORDER] After:', entries.map(e => e.label || e.id));

  return entries.map(e => e.id);
}

export default sortQuestionIds;
